import { useEffect, useMemo, useRef, useState } from "react";

import type { MediaClip } from "../../api/media";
import type { ClipRef, Track } from "../../api/projects";
import { Compositor, effectsFromClip } from "../../preview/Compositor";
import { transitionIdOf } from "../../preview/transitions";
import { useEditor } from "../../store/editor";
import PreviewOverlays from "./PreviewOverlays";

/**
 * Preview con compositor WebGL2 + audio multi-pista.
 *
 * - Vídeo activo: el del clip de la pista superior que esté bajo el playhead.
 * - Audio: TODOS los clips activos de TODAS las pistas no silenciadas, mezclados
 *   por el navegador (cada <video>/<audio> contribuye con su propio output).
 * - Mute/Solo por pista respetados.
 */
export default function Preview({ library }: { library: MediaClip[] }) {
  const timeline = useEditor((s) => s.timeline);
  const playhead = useEditor((s) => s.playhead_ms);
  const setPlayhead = useEditor((s) => s.setPlayhead);
  const duration = useEditor((s) => s.durationMs());

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const compositorRef = useRef<Compositor | null>(null);
  const compositorError = useRef<string | null>(null);
  const videos = useRef<Record<number, HTMLVideoElement>>({});
  const images = useRef<Record<number, HTMLImageElement>>({});
  const [playing, setPlaying] = useState(false);
  const rafRef = useRef<number | null>(null);
  const lastT = useRef(performance.now());

  // Compositor
  useEffect(() => {
    if (!canvasRef.current || compositorRef.current) return;
    try {
      compositorRef.current = new Compositor(canvasRef.current);
    } catch (err: any) {
      compositorError.current = err?.message || String(err);
      console.error("Compositor init failed:", err);
    }
    return () => {
      compositorRef.current?.dispose();
      compositorRef.current = null;
    };
  }, []);

  // Pre-cargar LUTs
  useEffect(() => {
    const comp = compositorRef.current;
    if (!comp) return;
    const seen = new Set<string>();
    for (const t of timeline.tracks) {
      for (const c of t.clips) {
        for (const e of c.effects || []) {
          if ((e as any).type === "lut" && (e as any).file && !seen.has((e as any).file)) {
            seen.add((e as any).file);
            comp.preloadLut((e as any).file);
          }
        }
      }
    }
  }, [timeline]);

  const videoTracks = useMemo(
    () => timeline.tracks.filter((t) => t.type === "video"),
    [timeline.tracks],
  );

  const activeVideoClip = useMemo(() => {
    for (const t of videoTracks) {
      if ((t as any).hidden) continue;
      for (const c of t.clips) {
        if (playhead >= c.start && playhead < c.start + (c.out - c.in)) return c;
      }
    }
    return null;
  }, [videoTracks, playhead]);

  /**
   * Detecta si estamos en la región de transición saliente del clip activo:
   * playhead ∈ [end - tr_dur, end] y existe un clip "B" siguiente.
   */
  const activeTransition = useMemo(() => {
    if (!activeVideoClip || !activeVideoClip.transitionOut) return null;
    const aEnd = activeVideoClip.start + (activeVideoClip.out - activeVideoClip.in);
    const trDur = activeVideoClip.transitionOut.duration_ms;
    const trStart = aEnd - trDur;
    if (playhead < trStart) return null;
    // Buscar el siguiente clip en la misma pista (start > A.start, mínimo)
    let nextClip = null;
    for (const t of videoTracks) {
      for (const c of t.clips) {
        if (c.id === activeVideoClip.id) continue;
        if (c.start <= activeVideoClip.start) continue;
        if (!nextClip || c.start < (nextClip as any).start) nextClip = c;
      }
    }
    if (!nextClip) return null;
    const progress = Math.max(0, Math.min(1, (playhead - trStart) / trDur));
    return {
      from: activeVideoClip,
      to: nextClip as typeof activeVideoClip,
      progress,
      transitionId: transitionIdOf(activeVideoClip.transitionOut.type),
    };
  }, [activeVideoClip, videoTracks, playhead]);

  const libraryById = useMemo(() => {
    const m: Record<number, MediaClip> = {};
    for (const c of library) m[c.id] = c;
    return m;
  }, [library]);

  // RAF loop — lee el playhead actual con getState() para evitar el problema
  // del closure rancio (sino tick capturaría el playhead inicial y nunca avanzaría)
  useEffect(() => {
    if (!playing) return;
    lastT.current = performance.now();
    function tick(now: number) {
      const dt = now - lastT.current;
      lastT.current = now;
      const current = useEditor.getState().playhead_ms;
      const next = current + dt;
      if (next >= duration) {
        setPlayhead(duration);
        setPlaying(false);
        return;
      }
      setPlayhead(next);
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [playing, duration, setPlayhead]);

  /** Resuelve el elemento source (video/img) para un clip, sincronizando currentTime. */
  function sourceFor(clip: any): HTMLVideoElement | HTMLImageElement | undefined {
    const media = libraryById[clip.clipId];
    if (!media) return undefined;
    if (media.type === "image") return images.current[clip.clipId];
    const vid = videos.current[clip.clipId];
    if (vid) {
      const clipTime = (playhead - clip.start + clip.in) / 1000;
      if (Math.abs(vid.currentTime - clipTime) > 0.1) vid.currentTime = clipTime;
    }
    return vid;
  }

  // Render frame (canvas) — transición si toca, sino sólo el clip activo
  useEffect(() => {
    const comp = compositorRef.current;
    if (!comp) return;

    if (activeTransition) {
      // Para el clip B necesitamos sincronizar a su tiempo de entrada en B.
      // El playhead está en [B.start, B.start + tr_dur). Su currentTime = B.in.
      const A = activeTransition.from;
      const B = activeTransition.to;
      const srcA = sourceFor(A);
      // Para B, simulamos un clip A's perspective: como B aún no ha "empezado oficialmente"
      // hasta B.start, el tiempo dentro del medio = B.in + (playhead - B.start) si > 0
      const tInB = Math.max(0, (playhead - B.start) / 1000) + B.in / 1000;
      const mediaB = libraryById[B.clipId];
      let srcB: HTMLVideoElement | HTMLImageElement | undefined;
      if (mediaB) {
        if (mediaB.type === "image") {
          srcB = images.current[B.clipId];
        } else {
          const vidB = videos.current[B.clipId];
          if (vidB && Math.abs(vidB.currentTime - tInB) > 0.1) vidB.currentTime = tInB;
          srcB = vidB;
        }
      }
      if (srcA && srcB) {
        comp.renderTransition(srcA, srcB, activeTransition.transitionId, activeTransition.progress);
        return;
      }
    }

    if (!activeVideoClip) { comp.clear(); return; }
    const source = sourceFor(activeVideoClip);
    if (!source) { comp.clear(); return; }
    comp.render(source, effectsFromClip(activeVideoClip.effects));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playhead, activeVideoClip, activeTransition, timeline, libraryById]);

  /**
   * Determina los clips audibles (uno por pista, el que esté bajo el playhead)
   * respetando muted/solo.
   */
  function getAudibleClips(): { track: Track; clip: ClipRef }[] {
    const anySolo = timeline.tracks.some((t: any) => t.solo === true);
    const result: { track: Track; clip: ClipRef }[] = [];
    for (const t of timeline.tracks) {
      if ((t as any).muted === true) continue;
      if (anySolo && (t as any).solo !== true) continue;
      // Sólo vídeo y audio aportan audio
      for (const c of t.clips) {
        if (playhead >= c.start && playhead < c.start + (c.out - c.in)) {
          result.push({ track: t, clip: c });
          break;
        }
      }
    }
    return result;
  }

  // Sync de audio al cambiar play / activeClips (no en cada frame)
  // Recalculamos la "huella" de clips activos para detectar cambios.
  const audibleSig = useMemo(() => {
    const audible = getAudibleClips();
    return audible.map((a) => a.clip.id).join("|");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeline, playhead, playing]);

  useEffect(() => {
    // Pausar todos primero
    Object.values(videos.current).forEach((v) => v.pause());
    if (!playing) return;

    const audible = getAudibleClips();
    // ¿Hay otra pista (no ducking) que esté sonando ahora? si sí, las "ducked" bajan
    const anyNonDuckedAudible = audible.some(({ track }) => (track as any).ducked !== true);
    for (const { track, clip } of audible) {
      const media = libraryById[clip.clipId];
      if (!media || media.type === "image") continue;
      const v = videos.current[clip.clipId];
      if (!v) continue;
      const clipTime = (playhead - clip.start + clip.in) / 1000;
      v.currentTime = clipTime;
      v.muted = false;
      const baseVol = track.type === "audio" ? (track as any).volume ?? 1 : 1;
      const clipVol = (clip as any).volume ?? 1;
      let vol = baseVol * clipVol;
      // Ducking: si esta pista tiene ducked=true y hay OTRA pista sin ducking sonando, bajar
      if ((track as any).ducked === true && anyNonDuckedAudible) {
        vol *= 0.18;  // -15 dB aprox
      }
      v.volume = Math.max(0, Math.min(1, vol));
      v.play().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, audibleSig]);

  const seconds = (playhead / 1000).toFixed(2);
  const total = (duration / 1000).toFixed(2);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="preview-canvas"
        style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
      />

      <PreviewOverlays
        canvas={canvasRef.current}
        videoWidth={canvasRef.current?.width || timeline.width}
        videoHeight={canvasRef.current?.height || timeline.height}
      />

      <div style={{ display: "none" }}>
        {library.map((c) => {
          if (c.type === "image" && c.original_url) {
            return (
              <img key={c.id} src={c.original_url} crossOrigin="anonymous"
                   ref={(el) => { if (el) images.current[c.id] = el; }} alt="" />
            );
          }
          if ((c.type === "video" || c.type === "audio") && (c.proxy_url || c.original_url)) {
            return (
              <video key={c.id} src={c.proxy_url || c.original_url!}
                     preload="auto" muted crossOrigin="anonymous"
                     ref={(el) => { if (el) videos.current[c.id] = el; }} />
            );
          }
          return null;
        })}
      </div>

      {compositorError.current && (
        <div style={{
          position: "absolute", top: 20, left: 20, right: 20,
          padding: 16, background: "rgba(255,0,0,0.15)",
          border: "1px solid var(--danger)", borderRadius: 6,
          fontSize: 13, color: "var(--danger)",
        }}>
          Preview WebGL2 no disponible: {compositorError.current}
        </div>
      )}

      <div style={{
        position: "absolute", bottom: 12, left: 12, right: 12,
        display: "flex", alignItems: "center", gap: 12,
        background: "rgba(0,0,0,0.55)", padding: "8px 12px", borderRadius: 8,
      }}>
        <button onClick={() => setPlaying((p) => !p)}>{playing ? "❚❚" : "▶"}</button>
        <input
          type="range" min={0} max={Math.max(duration, 1)} value={playhead}
          onChange={(e) => setPlayhead(Number(e.target.value))}
          style={{ flex: 1 }}
        />
        <span style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
          {seconds} / {total}s
        </span>
      </div>
    </>
  );
}
